/**
 * Client for the server-side duplicate-clustering endpoint (/api/dedup/documents).
 * The server clusters from the persisted fingerprint columns only (no blob reads),
 * scoped to the caller's account. Returns the same Map<docId, DupGroupInfo> shape the
 * in-browser computeDuplicateGroups() produces, so the UI is identical either way.
 * Returns null on any failure → the caller falls back to local computation.
 */
import { DupGroupInfo } from './documentFingerprint';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface DedupRow {
  document_id: number;
  group_id: number;
  original_id: number;
  is_original: boolean;
  match_pct: number;
}

export async function fetchDedupGroups(): Promise<Map<number, DupGroupInfo> | null> {
  const token = localStorage.getItem('akt_auth_token');
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/dedup/documents`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const groups: DedupRow[] = data?.groups || [];
    const map = new Map<number, DupGroupInfo>();
    for (const g of groups) {
      map.set(g.document_id, {
        groupId: g.group_id,
        originalId: g.original_id,
        isOriginal: g.is_original,
        matchPct: g.match_pct,
      });
    }
    return map;
  } catch {
    return null;
  }
}

/** The user confirms `documentId` is NOT a duplicate of `otherIds` (stored per pair). */
export async function markNotDuplicate(documentId: number, otherIds: number[]): Promise<boolean> {
  const token = localStorage.getItem('akt_auth_token');
  if (!token) return false;
  try {
    const res = await fetch(`${API_BASE}/dedup/not-duplicate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_id: documentId, other_ids: otherIds }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
